import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {UtilsService} from '@services/utils/utils.service';
import {User} from '@models/user/user';
import {Role} from '@models/user/role';
import { HttpResponse } from '@angular/common/http';
import {PagesHelperService} from '@services/pages/pages-helper.service';
import {PageGlobalParameters} from '@models/pages/page-global-parameters';
import {NotificationService} from '@services/notification/notification.service';
import {TranslateService} from '@ngx-translate/core';
import {InteractiveDataAnalysisConfig} from '@models/pages/interactive-data-analysis/interactive-data-analysis-config';
import {MenuItem} from '@models/sidebar/menu-item';
import {BreadcrumbService} from '@services/breadcrumb/breadcrumb.service';
import {FirebaseAuthenticationService} from '@services/firebase/auth/firebase-authentication.service';
import {MenuNotificationService} from '@services/menu-notification/menu-notification.service';

@Component({
    selector: 'scitis-interactive-data-analysis-container',
    templateUrl: './interactive-data-analysis-container.component.html',
    styleUrls: ['./interactive-data-analysis-container.component.scss'],
    standalone: false
})
export class InteractiveDataAnalysisContainerComponent implements OnInit {
  public pageGlobalParameters: PageGlobalParameters = new PageGlobalParameters();
  public isAdminOrHigher: boolean = false;
  public isHigherThanAdmin: boolean = false;
  public isCollapsed: boolean = false;
  public exportStartedWithType: string = '';
  public useColorPalette: boolean = false;
  public updatedInteractiveDataAnalysisConfig: InteractiveDataAnalysisConfig = new InteractiveDataAnalysisConfig();
  public routes: MenuItem[] = [];
  private CURRENT_PAGE_NAME = 'InteractiveDataAnalysisContainerComponent';
  private readonly usingColorPaletteMessage = '';
  private readonly notUsingColorPaletteMessage = '';

  constructor(private activatedRoute: ActivatedRoute,
              private firebaseAuthenticationService: FirebaseAuthenticationService,
              private utilsService: UtilsService,
              private pagesHelperService: PagesHelperService,
              private notification: NotificationService,
              private translate: TranslateService,
              private breadcrumbService: BreadcrumbService,
              private menuNotificationService: MenuNotificationService) {
    this.firebaseAuthenticationService.getLoggedUserDetails().subscribe({
      next: (user: User) => {
        if (user.parentId) {
          const userRole = new Role(user);
          this.isAdminOrHigher = this.utilsService.isAdminOrHigher(userRole.isAdminRole, userRole.isTechAdminRole, userRole.isSuperAdminRole);
          this.isHigherThanAdmin = this.utilsService.isHigherThanAdmin(userRole.isTechAdminRole, userRole.isSuperAdminRole);
        }
      },
      error: (error: HttpResponse<any>) => {
        console.error(`${this.CURRENT_PAGE_NAME}. Error getting current user details.`, JSON.stringify(error));
      }
    });
    this.usingColorPaletteMessage = this.translate.instant('Color palette is being used.');
    this.notUsingColorPaletteMessage = this.translate.instant('Color palette is not being used.');
  }

  ngOnInit(): void {
    this.pageGlobalParameters = this.pagesHelperService.getPageDetailsFromUrl(this.activatedRoute.snapshot.paramMap);
    this.generateRoutes(this.pageGlobalParameters.associatedMenuItem);
    const pageMenuItem = this.pageGlobalParameters.associatedMenuItem;
    const machineMenuItem = pageMenuItem?.menuItemParent;
    if (machineMenuItem?.id && pageMenuItem?.id) {
      this.menuNotificationService.broadcastMachinePageSelection(machineMenuItem, pageMenuItem);
    }
  }

  public onCollapseSettingsChange() {
    this.isCollapsed = !this.isCollapsed;
  }

  public onToggleUseColorPalette(): void {
    this.useColorPalette = !this.useColorPalette;
    const message = this.useColorPalette ? this.usingColorPaletteMessage : this.notUsingColorPaletteMessage;
    this.notification.success(message, this.CURRENT_PAGE_NAME, true);
  }

  onChartDataExportTriggered($event: string) {
    if (!$event || $event.isNullOrWhitespace()) {
      return;
    }
    this.exportStartedWithType = $event;
  }

  onInteractiveDataAnalysisConfigUpdated(updatedInteractiveDataAnalysisConfig: InteractiveDataAnalysisConfig): void {
    this.updatedInteractiveDataAnalysisConfig = {...updatedInteractiveDataAnalysisConfig};
  }

  private generateRoutes(menuItem: MenuItem) {
    this.routes = this.breadcrumbService.generateRoutes(menuItem);
  }

}
